import classNames from 'classnames';
import React, {ReactElement, useCallback, useMemo, useState} from 'react';

import Col from '../../common/Col';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {useAppDispatch} from '../../store';
import {GameLogicError} from '../errors';
import {Actions} from '../gameSlice';
import {useGameStatus} from '../hooks';
import {formatMoney} from '../mechanics';
import {useAllLandUses} from '../resources/landUses';
import {useAllPlaces} from '../resources/places';
import {LandUse, Place} from '../types';
import ArrowButton from './buttons/ArrowButton';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';
import WheelOfFate from './WheelOfFate';

enum PreparationPhase {
  goal = 0,
  place = 1,
  landUse = 2,
}

type Props = {};

const Preparation = ({}: Props): ReactElement => {
  const gameStatus = useGameStatus();
  const dispatch = useAppDispatch();
  const {isMobile} = useBreakpoint('mobile');

  const [currentPhase, setCurrentPhase] = useState<PreparationPhase>(
    gameStatus.place ? PreparationPhase.landUse : PreparationPhase.goal
  );
  const [landUse, setLandUse] = useState<LandUse>();
  const [spinning, setSpinning] = useState(false);

  const places = useAllPlaces();
  const landUses = useAllLandUses();
  const place = gameStatus.place;

  const canContinue = useMemo(() => {
    if (spinning) return false;
    switch (currentPhase) {
      case PreparationPhase.goal:
        return true;
      case PreparationPhase.place:
        return !!place;
      case PreparationPhase.landUse:
        return !!landUse;
    }
  }, [currentPhase, landUse, place, spinning]);

  const handleBack = useCallback(() => {
    switch (currentPhase) {
      case PreparationPhase.goal:
        return dispatch(Actions.restart());
      case PreparationPhase.place:
        setCurrentPhase(PreparationPhase.goal);
        break;
      case PreparationPhase.landUse:
        setCurrentPhase(PreparationPhase.place);
        break;
    }
  }, [currentPhase, dispatch]);

  const handleContinue = useCallback(() => {
    switch (currentPhase) {
      case PreparationPhase.goal:
        setCurrentPhase(PreparationPhase.place);
        break;
      case PreparationPhase.place:
        setCurrentPhase(PreparationPhase.landUse);
        break;
      case PreparationPhase.landUse:
        if (!place || !landUse) throw new GameLogicError('Place or Land Use not defined');
        dispatch(Actions.prepareGame(landUse));
        break;
    }
  }, [currentPhase, dispatch, place, landUse]);

  const handlePlaceChosen = useCallback(
    (choice: any) => {
      const place = GameLogicError.assertNonNullish<Place>(choice);
      dispatch(Actions.setPlace(place));
    },
    [dispatch]
  );

  const handleLandUseChosen = useCallback((choice: any) => {
    const landUse = GameLogicError.assertNonNullish<LandUse>(choice);
    setLandUse(landUse);
  }, []);

  return (
    <MessageBox
      top={currentPhase === PreparationPhase.goal && <SimplePill>Game Goals</SimplePill>}
      topLeft={<ArrowButton direction="left" onClick={handleBack} light />}
      bottom={
        canContinue && (
          <div
            className={classNames(
              'flex',
              isMobile || currentPhase === PreparationPhase.goal ? 'justify-center' : 'justify-end mr-72'
            )}
          >
            <ArrowButton direction="right" onClick={handleContinue}>
              Continue
            </ArrowButton>
          </div>
        )
      }
    >
      {currentPhase === PreparationPhase.goal && (
        <Col className="space-y-md font-medium mt-md text-2xl text-center mobile:text-lg">
          <div>
            The local government has chosen your farm to grow a biomass crop and produce enough fuel to heat a local
            school.
          </div>
          <div>
            You will be in charge of <span className="text-primary-600">{gameStatus.landSize} hectares of land</span>{' '}
            and receive <span className="text-primary-600">{formatMoney(gameStatus.money)}</span> as start up fund.
          </div>
          <div className="border-2 rounded-3xl p-xs w-full border-primary-300 text-xl bg-primary-100 mobile:text-base">
            Your goal is to produce{' '}
            <span className="text-primary-600">{gameStatus.productionGoal} tonnes of biomass</span> in{' '}
            <span className="text-primary-600">10 years</span> and make as big a profit as possible.
          </div>
        </Col>
      )}
      {currentPhase === PreparationPhase.place && (
        <div
          className={classNames(
            'grid gap-x-md grid-cols-2 min-h-[55rem] font-medium text-2xl items-center',
            'mobile:flex mobile:flex-col mobile:min-h-fit mobile:text-lg'
          )}
        >
          {place ? (
            <Col className="self-end mobile:self-start">
              <div className="text-primary-600">You have selected</div>
              <div className="text-huge leading-tight mb-sm mobile:text-2xl">{place.name}</div>
              <div className="text-2xl mobile:text-lg">Location: {place.name}, UK</div>
              {!isMobile && <div className="text-xl">{place.description}</div>}
            </Col>
          ) : (
            <p className="text-center">Spin the wheel of fate to pick your location</p>
          )}
          <WheelOfFate
            initialChoice={place}
            choices={places}
            wheelClass="bg-places"
            onChosen={handlePlaceChosen}
            onSpinStart={() => setSpinning(true)}
            onSpinEnd={() => setSpinning(false)}
          />
          {place && isMobile && <div className="text-lg">{place.description}</div>}
        </div>
      )}
      {currentPhase === PreparationPhase.landUse && (
        <div
          className={classNames(
            'grid gap-x-md grid-cols-2 min-h-[55rem] font-medium text-2xl items-center',
            'mobile:flex mobile:flex-col mobile:min-h-fit mobile:text-lg'
          )}
        >
          {landUse ? (
            <Col className="self-end mobile:self-start">
              <div className="text-primary-600">You have selected</div>
              <div className="text-huge leading-tight mb-sm mobile:text-2xl">{landUse.name}</div>
              {!isMobile && <div className="text-xl">{landUse.description}</div>}
            </Col>
          ) : (
            <p className="text-center">Spin the wheel of fate to pick your current land use</p>
          )}
          <WheelOfFate
            initialChoice={landUse}
            choices={landUses}
            wheelClass="bg-landUses"
            onChosen={handleLandUseChosen}
            onSpinStart={() => setSpinning(true)}
            onSpinEnd={() => setSpinning(false)}
          />
          {landUse && isMobile && <div className="text-lg">{landUse.description}</div>}
        </div>
      )}
    </MessageBox>
  );
};

export default Preparation;
